/**
 * DateInput View Component (Web)
 *
 * Uses native HTML `<input type="date">` for the browser's built-in calendar
 * date picker. The browser displays dates in the user's locale format while
 * the value is always YYYY-MM-DD (ISO). Styled to match Gluestack Input
 * for visual consistency.
 * @module components/DateInput
 */

import { useCallback } from "react";

/** Minimum selectable date (year 1940) */
const MIN_DATE = "1940-01-01";

/**
 * Native date-picker colour-scheme style.
 * design-system: dynamic-style waiver — data-driven value (theme-driven native picker scheme)
 * @param isDark - Whether dark theme is active
 */
const colorSchemeStyle = (isDark?: boolean) => ({
  colorScheme: isDark ? "dark" : "light",
});

/**
 * Returns today's date as YYYY-MM-DD string for the max attribute
 */
const getTodayISO = (): string => new Date().toISOString().split("T")[0];

/** Props shared with the native DateInputView for cross-platform compatibility */
export interface DateInputViewProps {
  /** Current value in YYYY-MM-DD format */
  readonly value: string;
  /** Called when the text value changes (string, not HTML event) */
  readonly onChangeText: (value: string) => void;
  /** Called when input loses focus */
  readonly onBlur?: () => void;
  /** Test identifier for the input */
  readonly testID?: string;
  /** Whether dark theme is active — controls the native calendar picker color scheme */
  readonly isDark?: boolean;
  /**
   * Accessible name for the control.
   *
   * The molecule renders a bare `<input type="date">` on web and a masked text
   * field on native, and neither carries a name of its own — the visible label
   * beside it is not associated with either. Without this a screen reader
   * announces an unnamed date field (SE-7316).
   */
  readonly accessibilityLabel?: string;
  /**
   * Earliest selectable date, ISO `YYYY-MM-DD`. Defaults to 1940-01-01.
   */
  readonly minDate?: string;
  /**
   * Latest selectable date, ISO `YYYY-MM-DD`. Defaults to TODAY, because the
   * two original consumers ask for dates that have already happened — a date
   * of birth and a historical filter range.
   *
   * SE-7316 made it a prop rather than a constant: a fixture is normally in
   * the FUTURE (pairings publish six to eight weeks ahead), so the default
   * would have made the primary case unreachable. Optional and defaulted so
   * both existing call sites keep today's behaviour untouched.
   */
  readonly maxDate?: string;
}

/**
 * Web date input using the browser's native calendar date picker.
 * Adapts the string-based onChangeText callback to an HTML change event
 * handler internally.
 * @param root0 - Component props
 * @param root0.value - Current date value in ISO format
 * @param root0.onChangeText - Called with the new date string value
 * @param root0.onBlur - Called when input loses focus
 * @param root0.testID - Test identifier for the input
 * @param root0.isDark - Whether dark theme is active
 * @param root0.accessibilityLabel - Accessible name for the control
 * @param root0.minDate - Earliest selectable date (ISO), default 1940-01-01
 * @param root0.maxDate - Latest selectable date (ISO), default today
 */
const DateInputView = ({
  value,
  onChangeText,
  onBlur,
  testID,
  isDark,
  accessibilityLabel,
  minDate,
  maxDate,
}: DateInputViewProps) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChangeText(e.target.value);
    },
    [onChangeText]
  );

  return (
    <input
      type="date"
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      min={minDate ?? MIN_DATE}
      max={maxDate ?? getTodayISO()}
      aria-label={accessibilityLabel}
      data-testid={testID}
      className="h-10 w-full rounded-sm border border-outline-strong bg-transparent px-3 font-body text-title-sm text-content-primary outline-none focus:border-needs-semantic-primary-500 dark:border-needs-semantic-outline-600 dark:text-white"
      // eslint-disable-next-line no-restricted-syntax -- native <input type=date> color-scheme; no atom renders a browser date picker and colorScheme has no closed token
      style={colorSchemeStyle(isDark)}
    />
  );
};

export default DateInputView;
